import {useSortable} from '@dnd-kit/sortable';
import {Button} from '@wandb/weave/components/Button';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {useIsMobile} from '../../../util/panelbankFlow';
import {getPanelSpec} from '../../../util/panelTypes';
import {useFeatureFlagVegaEnabled} from '../../../util/useFeatureFlag';
import {getDndSortablePanelId} from '../../PanelBank/sectionLayout/utils';
import {useWorkspaceSectionContext} from '../../PanelBank/sectionLayout/WorkspaceSectionContextProvider';
import {PanelRunButton} from '../../PanelMarimo/PanelRunButton';
import {CompareZoomControl} from '../../PanelMediaBrowser/zoom/CompareZoomControl';
import {PanelViewVegaSpec} from '../common/PanelViewVegaSpec';
import {PanelFullScreenButton} from '../FullScreenPanel/PanelFullScreenButton';
import {usePanelContext} from '../PanelContextProvider';
import {PanelEditButton} from '../PanelEditButton';
import {PanelRunMetricButton} from '../PanelRunMetricButton';
import {WorkspacePanelOverflowMenu} from './WorkspacePanelOverflowMenu';

export const WorkspacePanelHeader = () => {
  const isMobileLayout = useIsMobile();
  const {defaultMoveToSectionRef} = useWorkspaceSectionContext();
  const {isReadOnly, panelBankSectionConfigRef, panelRef, panel} =
    usePanelContext();

  const panelType = panel.viewType;
  const panelSpec = useMemo(() => getPanelSpec(panelType), [panelType]);

  const {entityName} = useProjectParams();
  const vegaPanelEnabled = useFeatureFlagVegaEnabled(entityName);

  const [isOverflowMenuActive, setIsOverflowMenuActive] = useState(false);
  const [isZoomMenuActive, setIsZoomMenuActive] = useState(false);

  const {setActivatorNodeRef, listeners, attributes} = useSortable({
    id: getDndSortablePanelId(panelRef),
  });

  return (
    <TailwindContents>
      <div
        className={twMerge(
          'relative flex shrink-0',
          'mb-4 min-h-[24px]',
          // on a regular workspace OR report where multiple panels are visible, we want to only show
          // the actionable left side items when the mouse is over the panel or a control inside it has focus. it should always be visible in full screen where we only
          // show 1 panel
          'opacity-0 focus-within:opacity-100 hover:opacity-100',
          // since we hide the actionable items until mouse hover on a regular workspace or report where we display multiple panels,
          // we still want to show the actionable items when overflow menu is active
          (isOverflowMenuActive || isZoomMenuActive) && 'opacity-100'
        )}
        data-test="panel-header">
        {!isReadOnly && (
          <Button
            ref={setActivatorNodeRef}
            {...listeners}
            {...attributes}
            aria-label="Drag panel"
            data-dd-action-name="Drag panel"
            className="absolute left-0 top-0 m-0 cursor-grab active:cursor-grabbing"
            icon="drag-grip"
            size="small"
            variant="ghost"
          />
        )}
        <div
          className={twMerge(
            // on a regular workspace OR report where multiple panels are visible, the actions should be rendered on top
            // of the drag handle and there's no back button to consider
            'absolute right-8'
          )}>
          <div className={twMerge('flex gap-8')}>
            <PanelRunMetricButton
              panelBankSectionConfigRef={panelBankSectionConfigRef}
              panelRef={panelRef}
              panelType={panelType}
            />
            {isReadOnly && <PanelViewVegaSpec panelType={panelType} />}
            {!isMobileLayout && (
              <PanelFullScreenButton
                panelBankSectionConfigRef={panelBankSectionConfigRef}
              />
            )}
            {panelType === 'Media Browser' && (
              <CompareZoomControl
                view="workspace"
                onMenuOpenChange={setIsZoomMenuActive}
              />
            )}
            {!isReadOnly &&
              !isMobileLayout &&
              !panelSpec.noEditMode &&
              (panelType !== 'Vega' || vegaPanelEnabled) && <PanelEditButton />}
            {panelType === 'Marimo' && !isMobileLayout && (
              <PanelRunButton panelRef={panelRef} buttonSize="sm" />
            )}
            {!isReadOnly && panelBankSectionConfigRef != null && (
              <WorkspacePanelOverflowMenu
                defaultMoveToSectionRef={defaultMoveToSectionRef}
                panelSpec={panelSpec}
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                isOpen={isOverflowMenuActive}
                setIsOpen={setIsOverflowMenuActive}
              />
            )}
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};
